/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `event` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class EventMesg : Mesg {
    public constructor() : super("Event", Profile.MesgNum.EVENT)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** event. */
    public var event: Event?
        get() = (getFieldValue(EVENT_FIELD_NUM) as? UByte)?.let { Event.fromValue(it) }
        set(value) = setFieldValue(EVENT_FIELD_NUM, value?.value)

    /** event_type. */
    public var eventType: EventType?
        get() = (getFieldValue(EVENT_TYPE_FIELD_NUM) as? UByte)?.let { EventType.fromValue(it) }
        set(value) = setFieldValue(EVENT_TYPE_FIELD_NUM, value?.value)

    /** data16. */
    public var data16: UShort?
        get() = getFieldValue(DATA16_FIELD_NUM) as? UShort
        set(value) = setFieldValue(DATA16_FIELD_NUM, value)

    /** data. */
    public var data: UInt?
        get() = getFieldValue(DATA_FIELD_NUM) as? UInt
        set(value) = setFieldValue(DATA_FIELD_NUM, value)

    /**
     * timer_trigger: what data means when this message selects it.
     */
    public val timerTrigger: TimerTrigger?
        get() = (getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("TimerTrigger")) as? UByte)?.let { TimerTrigger.fromValue(it) }

    public fun setTimerTrigger(value: TimerTrigger?) {
        setFieldValue(DATA_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("TimerTrigger"))
    }

    /**
     * course_point_index: what data means when this message selects it.
     */
    public val coursePointIndex: UShort?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("CoursePointIndex")) as? UShort

    public fun setCoursePointIndex(value: UShort?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("CoursePointIndex"))
    }

    /**
     * battery_level: what data means when this message selects it.
     */
    public val batteryLevel: Double?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("BatteryLevel")) as? Double

    public fun setBatteryLevel(value: Double?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("BatteryLevel"))
    }

    /**
     * virtual_partner_speed: what data means when this message selects it.
     */
    public val virtualPartnerSpeed: Double?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("VirtualPartnerSpeed")) as? Double

    public fun setVirtualPartnerSpeed(value: Double?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("VirtualPartnerSpeed"))
    }

    /**
     * hr_high_alert: what data means when this message selects it.
     */
    public val hrHighAlert: UByte?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("HrHighAlert")) as? UByte

    public fun setHrHighAlert(value: UByte?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("HrHighAlert"))
    }

    /**
     * hr_low_alert: what data means when this message selects it.
     */
    public val hrLowAlert: UByte?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("HrLowAlert")) as? UByte

    public fun setHrLowAlert(value: UByte?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("HrLowAlert"))
    }

    /**
     * speed_high_alert: what data means when this message selects it.
     */
    public val speedHighAlert: Double?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("SpeedHighAlert")) as? Double

    public fun setSpeedHighAlert(value: Double?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("SpeedHighAlert"))
    }

    /**
     * speed_low_alert: what data means when this message selects it.
     */
    public val speedLowAlert: Double?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("SpeedLowAlert")) as? Double

    public fun setSpeedLowAlert(value: Double?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("SpeedLowAlert"))
    }

    /**
     * cad_high_alert: what data means when this message selects it.
     */
    public val cadHighAlert: UShort?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("CadHighAlert")) as? UShort

    public fun setCadHighAlert(value: UShort?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("CadHighAlert"))
    }

    /**
     * cad_low_alert: what data means when this message selects it.
     */
    public val cadLowAlert: UShort?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("CadLowAlert")) as? UShort

    public fun setCadLowAlert(value: UShort?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("CadLowAlert"))
    }

    /**
     * power_high_alert: what data means when this message selects it.
     */
    public val powerHighAlert: UShort?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("PowerHighAlert")) as? UShort

    public fun setPowerHighAlert(value: UShort?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("PowerHighAlert"))
    }

    /**
     * power_low_alert: what data means when this message selects it.
     */
    public val powerLowAlert: UShort?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("PowerLowAlert")) as? UShort

    public fun setPowerLowAlert(value: UShort?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("PowerLowAlert"))
    }

    /**
     * time_duration_alert: what data means when this message selects it.
     */
    public val timeDurationAlert: Double?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("TimeDurationAlert")) as? Double

    public fun setTimeDurationAlert(value: Double?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("TimeDurationAlert"))
    }

    /**
     * distance_duration_alert: what data means when this message selects it.
     */
    public val distanceDurationAlert: Double?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("DistanceDurationAlert")) as? Double

    public fun setDistanceDurationAlert(value: Double?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("DistanceDurationAlert"))
    }

    /**
     * calorie_duration_alert: what data means when this message selects it.
     */
    public val calorieDurationAlert: UInt?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("CalorieDurationAlert")) as? UInt

    public fun setCalorieDurationAlert(value: UInt?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("CalorieDurationAlert"))
    }

    /**
     * fitness_equipment_state: what data means when this message selects it.
     */
    public val fitnessEquipmentState: FitnessEquipmentState?
        get() = (getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("FitnessEquipmentState")) as? UByte)?.let { FitnessEquipmentState.fromValue(it) }

    public fun setFitnessEquipmentState(value: FitnessEquipmentState?) {
        setFieldValue(DATA_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("FitnessEquipmentState"))
    }

    /**
     * sport_point: what data means when this message selects it.
     */
    public val sportPoint: UInt?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("SportPoint")) as? UInt

    public fun setSportPoint(value: UInt?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("SportPoint"))
    }

    /**
     * gear_change_data: what data means when this message selects it.
     */
    public val gearChangeData: UInt?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("GearChangeData")) as? UInt

    public fun setGearChangeData(value: UInt?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("GearChangeData"))
    }

    /**
     * rider_position: what data means when this message selects it.
     *
     * Indicates the rider position value.
     */
    public val riderPosition: RiderPositionType?
        get() = (getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("RiderPosition")) as? UByte)?.let { RiderPositionType.fromValue(it) }

    public fun setRiderPosition(value: RiderPositionType?) {
        setFieldValue(DATA_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("RiderPosition"))
    }

    /**
     * comm_timeout: what data means when this message selects it.
     */
    public val commTimeout: CommTimeoutType?
        get() = (getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("CommTimeout")) as? UShort)?.let { CommTimeoutType.fromValue(it) }

    public fun setCommTimeout(value: CommTimeoutType?) {
        setFieldValue(DATA_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("CommTimeout"))
    }

    /**
     * dive_alert: what data means when this message selects it.
     */
    public val diveAlert: DiveAlert?
        get() = (getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("DiveAlert")) as? UByte)?.let { DiveAlert.fromValue(it) }

    public fun setDiveAlert(value: DiveAlert?) {
        setFieldValue(DATA_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("DiveAlert"))
    }

    /**
     * auto_activity_detect_duration: what data means when this message selects it.
     */
    public val autoActivityDetectDuration: UShort?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("AutoActivityDetectDuration")) as? UShort

    public fun setAutoActivityDetectDuration(value: UShort?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("AutoActivityDetectDuration"))
    }

    /**
     * radar_threat_alert: what data means when this message selects it.
     *
     * The first byte is the radar_threat_level_max, the second byte is the radar_threat_count, third bytes is the average approach speed, and the 4th byte is the max approach speed
     */
    public val radarThreatAlert: UInt?
        get() = getFieldValue(DATA_FIELD_NUM, 0, SubFieldSelector.Named("RadarThreatAlert")) as? UInt

    public fun setRadarThreatAlert(value: UInt?) {
        setFieldValue(DATA_FIELD_NUM, value, 0, SubFieldSelector.Named("RadarThreatAlert"))
    }

    /** event_group. */
    public var eventGroup: UByte?
        get() = getFieldValue(EVENT_GROUP_FIELD_NUM) as? UByte
        set(value) = setFieldValue(EVENT_GROUP_FIELD_NUM, value)

    /** score. Do not populate directly. Autogenerated by decoder for sport_point subfield components */
    public var score: UShort?
        get() = getFieldValue(SCORE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(SCORE_FIELD_NUM, value)

    /** opponent_score. Do not populate directly. Autogenerated by decoder for sport_point subfield components */
    public var opponentScore: UShort?
        get() = getFieldValue(OPPONENT_SCORE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(OPPONENT_SCORE_FIELD_NUM, value)

    /** front_gear_num. Do not populate directly. Autogenerated by decoder for gear_change subfield components. Front gear number. 1 is innermost. */
    public var frontGearNum: UByte?
        get() = getFieldValue(FRONT_GEAR_NUM_FIELD_NUM) as? UByte
        set(value) = setFieldValue(FRONT_GEAR_NUM_FIELD_NUM, value)

    /** front_gear. Do not populate directly. Autogenerated by decoder for gear_change subfield components. Number of front teeth. */
    public var frontGear: UByte?
        get() = getFieldValue(FRONT_GEAR_FIELD_NUM) as? UByte
        set(value) = setFieldValue(FRONT_GEAR_FIELD_NUM, value)

    /** rear_gear_num. Do not populate directly. Autogenerated by decoder for gear_change subfield components. Rear gear number. 1 is innermost. */
    public var rearGearNum: UByte?
        get() = getFieldValue(REAR_GEAR_NUM_FIELD_NUM) as? UByte
        set(value) = setFieldValue(REAR_GEAR_NUM_FIELD_NUM, value)

    /** rear_gear. Do not populate directly. Autogenerated by decoder for gear_change subfield components. Number of rear teeth. */
    public var rearGear: UByte?
        get() = getFieldValue(REAR_GEAR_FIELD_NUM) as? UByte
        set(value) = setFieldValue(REAR_GEAR_FIELD_NUM, value)

    /** device_index. */
    public var deviceIndex: UByte?
        get() = getFieldValue(DEVICE_INDEX_FIELD_NUM) as? UByte
        set(value) = setFieldValue(DEVICE_INDEX_FIELD_NUM, value)

    /** activity_type. Activity Type associated with an auto_activity_detect event */
    public var activityType: ActivityType?
        get() = (getFieldValue(ACTIVITY_TYPE_FIELD_NUM) as? UByte)?.let { ActivityType.fromValue(it) }
        set(value) = setFieldValue(ACTIVITY_TYPE_FIELD_NUM, value?.value)

    /** start_timestamp, in s. Timestamp of when the event started */
    public var startTimestamp: Instant?
        get() = (getFieldValue(START_TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(START_TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /**
     * auto_activity_detect_start_timestamp: what start_timestamp means when this message selects it.
     *
     * Auto Activity Detect Start Timestamp.
     */
    public val autoActivityDetectStartTimestamp: Instant?
        get() = (getFieldValue(START_TIMESTAMP_FIELD_NUM, 0, SubFieldSelector.Named("AutoActivityDetectStartTimestamp")) as? UInt)?.let { FitDateTime.toInstant(it) }

    public fun setAutoActivityDetectStartTimestamp(value: Instant?) {
        setFieldValue(START_TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) }, 0, SubFieldSelector.Named("AutoActivityDetectStartTimestamp"))
    }

    /** radar_threat_level_max. Do not populate directly. Autogenerated by decoder for threat_alert subfield components. */
    public var radarThreatLevelMax: RadarThreatLevelType?
        get() = (getFieldValue(RADAR_THREAT_LEVEL_MAX_FIELD_NUM) as? UByte)?.let { RadarThreatLevelType.fromValue(it) }
        set(value) = setFieldValue(RADAR_THREAT_LEVEL_MAX_FIELD_NUM, value?.value)

    /** radar_threat_count. Do not populate directly. Autogenerated by decoder for threat_alert subfield components. */
    public var radarThreatCount: UByte?
        get() = getFieldValue(RADAR_THREAT_COUNT_FIELD_NUM) as? UByte
        set(value) = setFieldValue(RADAR_THREAT_COUNT_FIELD_NUM, value)

    /** radar_threat_avg_approach_speed, in m/s. Do not populate directly. Autogenerated by decoder for radar_threat_alert subfield components */
    public var radarThreatAvgApproachSpeed: Double?
        get() = getFieldValue(RADAR_THREAT_AVG_APPROACH_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(RADAR_THREAT_AVG_APPROACH_SPEED_FIELD_NUM, value)

    /** radar_threat_max_approach_speed, in m/s. Do not populate directly. Autogenerated by decoder for radar_threat_alert subfield components */
    public var radarThreatMaxApproachSpeed: Double?
        get() = getFieldValue(RADAR_THREAT_MAX_APPROACH_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(RADAR_THREAT_MAX_APPROACH_SPEED_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val EVENT_FIELD_NUM: UByte = 0u
        public const val EVENT_TYPE_FIELD_NUM: UByte = 1u
        public const val DATA16_FIELD_NUM: UByte = 2u
        public const val DATA_FIELD_NUM: UByte = 3u
        public const val EVENT_GROUP_FIELD_NUM: UByte = 4u
        public const val SCORE_FIELD_NUM: UByte = 7u
        public const val OPPONENT_SCORE_FIELD_NUM: UByte = 8u
        public const val FRONT_GEAR_NUM_FIELD_NUM: UByte = 9u
        public const val FRONT_GEAR_FIELD_NUM: UByte = 10u
        public const val REAR_GEAR_NUM_FIELD_NUM: UByte = 11u
        public const val REAR_GEAR_FIELD_NUM: UByte = 12u
        public const val DEVICE_INDEX_FIELD_NUM: UByte = 13u
        public const val ACTIVITY_TYPE_FIELD_NUM: UByte = 14u
        public const val START_TIMESTAMP_FIELD_NUM: UByte = 15u
        public const val RADAR_THREAT_LEVEL_MAX_FIELD_NUM: UByte = 21u
        public const val RADAR_THREAT_COUNT_FIELD_NUM: UByte = 22u
        public const val RADAR_THREAT_AVG_APPROACH_SPEED_FIELD_NUM: UByte = 23u
        public const val RADAR_THREAT_MAX_APPROACH_SPEED_FIELD_NUM: UByte = 24u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("Event", 0u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[1] = Field("EventType", 1u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[2] = Field("Data16", 2u, BaseType.UINT16, 1.0, 0.0, "", false)
                .addComponent(FieldComponent(3u, false, 16, 1.0, 0.0))
            fields[3] = Field("Data", 3u, BaseType.UINT32, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("TimerTrigger", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(0u, 0)
                    },
                )
                .addSubField(
                    SubField("CoursePointIndex", BaseType.UINT16, 1.0, 0.0, "").also { subField ->
                        subField.addMap(0u, 10)
                    },
                )
                .addSubField(
                    SubField("BatteryLevel", BaseType.UINT16, 1000.0, 0.0, "V").also { subField ->
                        subField.addMap(0u, 11)
                    },
                )
                .addSubField(
                    SubField("VirtualPartnerSpeed", BaseType.UINT16, 1000.0, 0.0, "m/s").also { subField ->
                        subField.addMap(0u, 12)
                    },
                )
                .addSubField(
                    SubField("HrHighAlert", BaseType.UINT8, 1.0, 0.0, "bpm").also { subField ->
                        subField.addMap(0u, 13)
                    },
                )
                .addSubField(
                    SubField("HrLowAlert", BaseType.UINT8, 1.0, 0.0, "bpm").also { subField ->
                        subField.addMap(0u, 14)
                    },
                )
                .addSubField(
                    SubField("SpeedHighAlert", BaseType.UINT32, 1000.0, 0.0, "m/s").also { subField ->
                        subField.addMap(0u, 15)
                    },
                )
                .addSubField(
                    SubField("SpeedLowAlert", BaseType.UINT32, 1000.0, 0.0, "m/s").also { subField ->
                        subField.addMap(0u, 16)
                    },
                )
                .addSubField(
                    SubField("CadHighAlert", BaseType.UINT16, 1.0, 0.0, "rpm").also { subField ->
                        subField.addMap(0u, 17)
                    },
                )
                .addSubField(
                    SubField("CadLowAlert", BaseType.UINT16, 1.0, 0.0, "rpm").also { subField ->
                        subField.addMap(0u, 18)
                    },
                )
                .addSubField(
                    SubField("PowerHighAlert", BaseType.UINT16, 1.0, 0.0, "watts").also { subField ->
                        subField.addMap(0u, 19)
                    },
                )
                .addSubField(
                    SubField("PowerLowAlert", BaseType.UINT16, 1.0, 0.0, "watts").also { subField ->
                        subField.addMap(0u, 20)
                    },
                )
                .addSubField(
                    SubField("TimeDurationAlert", BaseType.UINT32, 1000.0, 0.0, "s").also { subField ->
                        subField.addMap(0u, 23)
                    },
                )
                .addSubField(
                    SubField("DistanceDurationAlert", BaseType.UINT32, 100.0, 0.0, "m").also { subField ->
                        subField.addMap(0u, 24)
                    },
                )
                .addSubField(
                    SubField("CalorieDurationAlert", BaseType.UINT32, 1.0, 0.0, "calories").also { subField ->
                        subField.addMap(0u, 25)
                    },
                )
                .addSubField(
                    SubField("FitnessEquipmentState", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(0u, 27)
                    },
                )
                .addSubField(
                    SubField("SportPoint", BaseType.UINT32, 1.0, 0.0, "").also { subField ->
                        subField.addMap(0u, 33)
                        subField.addComponent(FieldComponent(7u, false, 16, 1.0, 0.0))
                        subField.addComponent(FieldComponent(8u, false, 16, 1.0, 0.0))
                    },
                )
                .addSubField(
                    SubField("GearChangeData", BaseType.UINT32, 1.0, 0.0, "").also { subField ->
                        subField.addMap(0u, 42)
                        subField.addMap(0u, 43)
                        subField.addComponent(FieldComponent(11u, false, 8, 1.0, 0.0))
                        subField.addComponent(FieldComponent(12u, false, 8, 1.0, 0.0))
                        subField.addComponent(FieldComponent(9u, false, 8, 1.0, 0.0))
                        subField.addComponent(FieldComponent(10u, false, 8, 1.0, 0.0))
                    },
                )
                .addSubField(
                    SubField("RiderPosition", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(0u, 44)
                    },
                )
                .addSubField(
                    SubField("CommTimeout", BaseType.UINT16, 1.0, 0.0, "").also { subField ->
                        subField.addMap(0u, 47)
                    },
                )
                .addSubField(
                    SubField("DiveAlert", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(0u, 56)
                    },
                )
                .addSubField(
                    SubField("AutoActivityDetectDuration", BaseType.UINT16, 1.0, 0.0, "min").also { subField ->
                        subField.addMap(0u, 54)
                    },
                )
                .addSubField(
                    SubField("RadarThreatAlert", BaseType.UINT32, 1.0, 0.0, "").also { subField ->
                        subField.addMap(0u, 75)
                        subField.addComponent(FieldComponent(21u, false, 8, 1.0, 0.0))
                        subField.addComponent(FieldComponent(22u, false, 8, 1.0, 0.0))
                        subField.addComponent(FieldComponent(23u, false, 8, 10.0, 0.0))
                        subField.addComponent(FieldComponent(24u, false, 8, 10.0, 0.0))
                    },
                )
            fields[4] = Field("EventGroup", 4u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[7] = Field("Score", 7u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[8] = Field("OpponentScore", 8u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[9] = Field("FrontGearNum", 9u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            fields[10] = Field("FrontGear", 10u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            fields[11] = Field("RearGearNum", 11u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            fields[12] = Field("RearGear", 12u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            fields[13] = Field("DeviceIndex", 13u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[14] = Field("ActivityType", 14u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[15] = Field("StartTimestamp", 15u, BaseType.UINT32, 1.0, 0.0, "s", false)
                .addSubField(
                    SubField("AutoActivityDetectStartTimestamp", BaseType.UINT32, 1.0, 0.0, "s").also { subField ->
                        subField.addMap(0u, 54)
                    },
                )
            fields[21] = Field("RadarThreatLevelMax", 21u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[22] = Field("RadarThreatCount", 22u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[23] = Field("RadarThreatAvgApproachSpeed", 23u, BaseType.UINT8, 10.0, 0.0, "m/s", false)
            fields[24] = Field("RadarThreatMaxApproachSpeed", 24u, BaseType.UINT8, 10.0, 0.0, "m/s", false)
            return fields
        }
    }
}
